import {
  makeStyles,
  Theme,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Box,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LinkIcon from '@material-ui/icons/Link';
import FilterListIcon from '@material-ui/icons/FilterList';
import PagesIcon from '@material-ui/icons/Pages';
import SearchIcon from '@material-ui/icons/Search';

import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';

import {
  SearchBar,
  SearchFilter,
  SearchResult,
  SearchPagination,
  useSearch,
} from '@backstage/plugin-search-react';
import {
  CatalogIcon,
  Content,
  DocsIcon,
  Page,
} from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { buildPortalPages, PortalPage } from '../../constants/portalPages';

const RADIUS = 16;

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === 'dark';

  const pageBg = isDark ? '#1a1a1a' : '#f7f8fa';
  const surface = isDark ? '#202226' : '#ffffff';
  const surfaceSoft = isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.02)';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const borderStrong = isDark ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.12)';
  const borderLight = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const textPrimary = isDark ? '#f4f4f5' : '#1d1d1f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.56)';
  const textMuted = isDark ? 'rgba(255,255,255,0.36)' : 'rgba(0,0,0,0.38)';
  const hover = isDark ? 'rgba(255,255,255,0.055)' : 'rgba(0,0,0,0.035)';
  const accent = isDark ? '#4da3ff' : '#007aff';
  const accentSoft = isDark ? 'rgba(77,163,255,0.13)' : 'rgba(0,122,255,0.08)';

  return {
    root: {
      width: '100%',
      maxWidth: 1400,
      margin: '0 auto',
      padding: '28px 34px 44px',
      minHeight: '100%',
      background: pageBg,

      '& *': {
        boxSizing: 'border-box',
      },

      [theme.breakpoints.down('sm')]: {
        padding: '22px 18px 36px',
      },
    },

    searchPanel: {
      borderRadius: RADIUS,
      background: surface,
      border: `1px solid ${border}`,
      boxShadow: isDark
        ? '0 10px 28px rgba(0,0,0,0.14)'
        : '0 10px 28px rgba(15,23,42,0.035)',
      overflow: 'hidden',
      marginBottom: 24,
    },

    bar: {
      padding: '9px 16px',
      marginTop: '-8px',

      '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiInput-root': {
        borderRadius: `${RADIUS}px !important`,
        background: isDark
          ? 'rgba(255,255,255,0.045) !important'
          : 'rgba(255,255,255,0.96) !important',
        border: `1px solid ${borderStrong} !important`,
        padding: '0px 14px !important',
        transition:
          'border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease !important',
      },

      '& .MuiInputBase-root.Mui-focused, & .MuiOutlinedInput-root.Mui-focused, & .MuiInput-root.Mui-focused': {
        borderColor: `${accent} !important`,
        boxShadow: isDark
          ? '0 0 0 4px rgba(77,163,255,0.18) !important'
          : '0 0 0 4px rgba(0,122,255,0.12) !important',
      },

      '& .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiInput-input': {
        color: `${textPrimary} !important`,
        fontSize: '14px !important',
      },

      '& .MuiSvgIcon-root': {
        color: `${textSecondary} !important`,
      },

      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
        display: 'none !important',
      },
    },

    mainGrid: {
      alignItems: 'flex-start',
    },

    filtersPanel: {
      position: 'sticky',
      top: 24,
      borderRadius: RADIUS,
      padding: 18,
      background: surface,
      border: `1px solid ${border}`,
      boxShadow: isDark
        ? '0 10px 28px rgba(0,0,0,0.14)'
        : '0 10px 28px rgba(15,23,42,0.035)',

      [theme.breakpoints.down('sm')]: {
        position: 'relative',
        top: 0,
      },

      '& .MuiPaper-root': {
        boxShadow: 'none !important',
        border: 'none !important',
        background: 'transparent !important',
      },
    },

    filtersHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginBottom: 18,
    },

    filtersIconBox: {
      width: 38,
      height: 38,
      borderRadius: RADIUS,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: accentSoft,

      '& svg': {
        color: accent,
        fontSize: 20,
      },
    },

    filtersTitle: {
      color: textPrimary,
      fontSize: 15,
      fontWeight: 760,
      letterSpacing: '-0.025em',
      lineHeight: 1.25,
    },

    filtersSubtitle: {
      color: textSecondary,
      fontSize: 12,
      marginTop: 2,
      lineHeight: 1.35,
    },

    typeMenu: {
      marginBottom: 16,
      borderRadius: RADIUS,
      border: `1px solid ${borderLight}`,
      background: surfaceSoft,
      overflow: 'hidden',
      padding: 4,
    },

    typeMenuItem: {
      borderRadius: RADIUS - 2,
      padding: '10px 14px',
      margin: 2,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      transition:
        'background-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease',

      '&:hover': {
        background: hover,
      },
    },

    typeMenuItemActive: {
      background: `${accentSoft} !important`,
      boxShadow: isDark
        ? '0 1px 3px rgba(0,0,0,0.12)'
        : '0 1px 3px rgba(0,0,0,0.06)',
    },

    typeMenuIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: '50%',
      flexShrink: 0,

      '& svg': {
        fontSize: 16,
        color: textSecondary,
      },
    },

    typeMenuIconActive: {
      '& svg': {
        color: `${accent} !important`,
      },
    },

    typeMenuLabel: {
      fontSize: 13,
      fontWeight: 600,
      color: textPrimary,
      lineHeight: 1.3,
    },

    typeMenuLabelActive: {
      color: `${accent} !important`,
      fontWeight: 700,
    },

    filters: {
      borderRadius: RADIUS,
      padding: 0,
      background: 'transparent',
      boxShadow: 'none',
      border: 'none',

      '& .MuiFormControl-root': {
        width: '100%',
      },
    },

    filter: {
      '& + &': {
        marginTop: theme.spacing(2.25),
      },

      '& .MuiInputLabel-root': {
        color: `${textSecondary} !important`,
        fontSize: '12.5px !important',
      },

      '& .MuiInputLabel-root.Mui-focused': {
        color: `${accent} !important`,
      },

      '& .MuiOutlinedInput-root': {
        borderRadius: `${RADIUS}px !important`,
        background: isDark
          ? 'rgba(255,255,255,0.04) !important'
          : 'rgba(255,255,255,0.92) !important',

        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: `${borderStrong} !important`,
        },

        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: isDark
            ? 'rgba(255,255,255,0.22) !important'
            : 'rgba(0,0,0,0.18) !important',
        },

        '&.Mui-focused': {
          boxShadow: isDark
            ? '0 0 0 4px rgba(77,163,255,0.18) !important'
            : '0 0 0 4px rgba(0,122,255,0.12) !important',
        },

        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: `${accent} !important`,
        },
      },

      '& .MuiSelect-root, & .MuiInputBase-input': {
        color: `${textPrimary} !important`,
        fontSize: '13px !important',
      },

      '& .MuiCheckbox-root': {
        color: `${textMuted} !important`,

        '&.Mui-checked': {
          color: `${accent} !important`,
        },
      },

      '& .MuiFormControlLabel-label': {
        color: `${textPrimary} !important`,
        fontSize: '13px !important',
      },
    },

    // ─── FIX ──────────────────────────────────────────────────────────────────
    // Always keep the Entity filter in the DOM (never unmount it) so the Kind /
    // Lifecycle filters below don't jump when the type selection changes.
    //
    // When techdocs is NOT selected the slot collapses to 0 height + 0 margin
    // via a smooth transition, so there is no permanent gap.
    //
    // max-height: 100px is safely larger than a single MUI v4 Select (~56–70px).
    // marginBottom replaces the `& + &` sibling margin that was previously
    // provided by the .filter class (the wrapper Box is not a .filter sibling).
    // ──────────────────────────────────────────────────────────────────────────
    entityFilterSlot: {
      maxHeight: 100,
      overflow: 'hidden',
      opacity: 1,
      pointerEvents: 'auto',
      marginBottom: theme.spacing(2.25),
      transition: [
        'max-height 0.22s ease',
        'opacity 0.18s ease',
        'margin-bottom 0.22s ease',
      ].join(', '),

      '&.hidden': {
        maxHeight: 0,
        opacity: 0,
        pointerEvents: 'none',
        marginBottom: 0,
      },
    },

    resultsPanel: {
      borderRadius: RADIUS,
      background: surface,
      border: `1px solid ${border}`,
      boxShadow: isDark
        ? '0 10px 28px rgba(0,0,0,0.14)'
        : '0 10px 28px rgba(15,23,42,0.035)',
      overflow: 'hidden',
    },

    resultsHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      borderBottom: `1px solid ${border}`,
      background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(248,250,252,0.9)',
    },

    resultsTitle: {
      color: textPrimary,
      fontSize: 15,
      fontWeight: 760,
      letterSpacing: '-0.025em',
    },

    resultsSubtitle: {
      color: textSecondary,
      fontSize: 12,
      marginTop: 2,
    },

    resultsBody: {
      padding: 18,

      '& .MuiPaper-root': {
        borderRadius: `${RADIUS}px !important`,
        border: `1px solid ${border} !important`,
        background: `${surface} !important`,
        boxShadow: 'none !important',
        overflow: 'hidden',
      },

      '& .MuiListItem-root': {
        borderRadius: `${RADIUS}px !important`,
      },

      '& .MuiTypography-root': {
        color: `${textPrimary} !important`,
      },

      '& .MuiTypography-body2, & .MuiTypography-caption': {
        color: `${textSecondary} !important`,
      },

      '& a': {
        color: `${accent} !important`,
      },
    },

    portalCard: {
      borderRadius: RADIUS,
      padding: '16px 18px',
      marginBottom: 16,
      background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.018)',
      border: `1px solid ${border}`,
      boxShadow: 'none',
    },

    portalHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 10,
    },

    portalHeaderIcon: {
      width: 28,
      height: 28,
      borderRadius: RADIUS,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: accentSoft,

      '& svg': {
        color: accent,
        fontSize: 16,
      },
    },

    portalLabel: {
      fontWeight: 760,
      letterSpacing: '0.11em',
      textTransform: 'uppercase',
      color: `${textMuted} !important`,
      fontSize: 10.5,
    },

    portalListItem: {
      borderRadius: `${RADIUS}px !important`,
      marginBottom: 6,
      padding: '10px 12px !important',
      transition:
        'background-color 0.16s ease, transform 0.16s ease, border-color 0.16s ease',
      border: `1px solid transparent`,

      '&:hover': {
        background: `${hover} !important`,
        borderColor: `${borderStrong}`,
        transform: 'translateY(-1px)',
      },
    },

    portalIcon: {
      minWidth: 34,

      '& svg': {
        color: `${accent} !important`,
        fontSize: 18,
      },
    },

    portalChip: {
      height: 22,
      borderRadius: RADIUS,
      fontSize: '10.5px !important',
      fontWeight: 650,
      background: isDark
        ? 'rgba(255,255,255,0.06) !important'
        : 'rgba(0,0,0,0.045) !important',
      color: `${textSecondary} !important`,
      border: `1px solid ${border}`,
    },

    pagination: {
      marginBottom: 14,

      '& .MuiTablePagination-root, & .MuiToolbar-root': {
        borderRadius: `${RADIUS}px !important`,
      },

      '& .MuiIconButton-root': {
        borderRadius: `${RADIUS}px !important`,
      },
    },
  };
});

const SearchPage = () => {
  const classes = useStyles();
  const { types, term, setTypes } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const navigate = useNavigate();

  const typeOptions = [
    { value: '', name: 'All', icon: <SearchIcon /> },
    { value: 'software-catalog', name: 'Software Catalog', icon: <CatalogIcon /> },
    { value: 'techdocs', name: 'Documentation', icon: <DocsIcon /> },
  ];

  const portalResults = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (q.length < 2) return [];

    return buildPortalPages(configApi)
      .filter(
        p =>
          p.label.toLowerCase().includes(q) ||
          p.section.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [term, configApi]);

  const handlePortalClick = (item: PortalPage) => {
    if (item.to.startsWith('msteams://')) {
      window.location.href = item.to;
    } else if (item.external) {
      window.open(item.to, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.to);
    }
  };

  return (
    <Page themeId="home">
      <Content>
        <Box className={classes.root}>
          <Paper className={classes.searchPanel}>
            <Box className={classes.bar}>
              <SearchBar />
            </Box>
          </Paper>

          <Grid container spacing={3} className={classes.mainGrid}>
            <Grid item xs={12} md={3}>
              <Box className={classes.filtersPanel}>
                <Box className={classes.filtersHeader}>
                  <Box className={classes.filtersIconBox}>
                    <FilterListIcon />
                  </Box>
                  <Box>
                    <Typography className={classes.filtersTitle}>
                      Filters
                    </Typography>
                    <Typography className={classes.filtersSubtitle}>
                      Narrow down your search results.
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.typeMenu} role="listbox" aria-label="Result type filter">
                  {typeOptions.map(opt => {
                    const isActive = opt.value === ''
                      ? types.length === 0
                      : types.includes(opt.value);
                    return (
                      <Box
                        key={opt.value || '__all__'}
                        role="option"
                        tabIndex={0}
                        aria-selected={isActive}
                        className={`${classes.typeMenuItem}${isActive ? ` ${classes.typeMenuItemActive}` : ''}`}
                        onClick={() => setTypes(opt.value ? [opt.value] : [])}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setTypes(opt.value ? [opt.value] : []);
                          }
                        }}
                      >
                        <Box className={`${classes.typeMenuIcon}${isActive ? ` ${classes.typeMenuIconActive}` : ''}`}>
                          {opt.icon}
                        </Box>
                        <Typography
                          className={`${classes.typeMenuLabel}${isActive ? ` ${classes.typeMenuLabelActive}` : ''}`}
                        >
                          {opt.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                <Paper className={classes.filters}>
                  {/*
                    Always render Entity — never conditionally unmount it.
                    When techdocs is not active, the slot smoothly collapses to
                    zero height + zero margin so no gap is left behind.
                    The `hidden` class drives the max-height / opacity / margin
                    transitions defined in entityFilterSlot above.
                  */}
                  <Box
                    className={`${classes.entityFilterSlot}${
                      types.includes('techdocs') ? '' : ' hidden'
                    }`}
                  >
                    <SearchFilter.Select
                      className={classes.filter}
                      label="Entity"
                      name="name"
                      values={async () => {
                        const { items } = await catalogApi.getEntities({
                          fields: ['metadata.name'],
                          filter: {
                            'metadata.annotations.backstage.io/techdocs-ref':
                              CATALOG_FILTER_EXISTS,
                          },
                        });
                        const names = items.map(
                          entity => entity.metadata.name,
                        );
                        names.sort();
                        return names;
                      }}
                    />
                  </Box>

                  <SearchFilter.Select
                    className={classes.filter}
                    label="Kind"
                    name="kind"
                    values={['Domain', 'System', 'Component', 'Resource', 'API', 'Template']}
                  />

                  <SearchFilter.Checkbox
                    className={classes.filter}
                    label="Lifecycle"
                    name="lifecycle"
                    values={['experimental', 'production']}
                  />
                </Paper>
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <Box className={classes.resultsPanel}>
                <Box className={classes.resultsHeader}>
                  <Box>
                    <Typography className={classes.resultsTitle}>
                      Results
                    </Typography>
                    <Typography className={classes.resultsSubtitle}>
                      {term.trim()
                        ? `Showing matches for "${term.trim()}"`
                        : 'Start typing to search across the portal.'}
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.resultsBody}>
                  {portalResults.length > 0 && (
                    <Paper className={classes.portalCard}>
                      <Box className={classes.portalHeader}>
                        <Box className={classes.portalHeaderIcon}>
                          <PagesIcon />
                        </Box>
                        <Typography className={classes.portalLabel}>
                          Portal Pages
                        </Typography>
                      </Box>

                      <List dense disablePadding>
                        {portalResults.map(item => (
                          <ListItem
                            button
                            key={item.label}
                            onClick={() => handlePortalClick(item)}
                            className={classes.portalListItem}
                          >
                            <ListItemIcon className={classes.portalIcon}>
                              {item.external ? (
                                <OpenInNewIcon fontSize="small" />
                              ) : (
                                <LinkIcon fontSize="small" />
                              )}
                            </ListItemIcon>

                            <ListItemText
                              primary={item.label}
                              secondary={item.section}
                              primaryTypographyProps={{
                                style: {
                                  fontWeight: 650,
                                  fontSize: 13.5,
                                },
                              }}
                              secondaryTypographyProps={{
                                style: {
                                  fontSize: 12,
                                },
                              }}
                            />

                            <Chip
                              label={item.section}
                              size="small"
                              className={classes.portalChip}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}

                  <Box className={classes.pagination}>
                    <SearchPagination />
                  </Box>

                  <SearchResult>
                    <CatalogSearchResultListItem icon={<CatalogIcon />} />
                    <TechDocsSearchResultListItem icon={<DocsIcon />} />
                  </SearchResult>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Content>
    </Page>
  );
};

export const searchPage = <SearchPage />;